import {ClickAwayListener, css, InputAdornment, Popper, useMediaQuery, useTheme} from '@mui/material';
import {
  GetStylesFunction,
  DateUtils,
  DlMuiCalendarIcon,
  DlMuiDatePicker,
  DlMuiDynamicIcon,
  DlMuiTextField,
  TranslateUtils,
} from '@wallaby-ui/core';
import classNames from 'classnames';
import {format, isValid, parseISO} from 'date-fns';
import React, {useCallback, useState} from 'react';
import {Placement} from 'react-bootstrap/esm/types';
import {useIntl} from 'react-intl';

import lightFilterNodeStyles from './LightFilterNodeStyles.less';
import {FilterConfig, useFormatters} from './LightFormatter';
import {LightInputLabel} from './LightInputLabel';
import {ValidateError} from './LightValidator';
import {FilterNodeTypeWrapper} from 'src/js/containers/filtering';
import {DlMuiDatePickerLayout, DlMuiPickersDay, dateRangeGetStyles} from 'src/js/containers/filtering/components/FilterNodeTypeDateRange';
import {DynamicFilter, FILTER_TYPE_DATE_ONLY_RANGE, FILTER_TYPE_DATE_RANGE, DateRangeFilter} from 'src/js/lib/filtering';
import {FilterNode} from 'src/js/model/filters_config';
import {FilterNodeTypeDateRangeCalendarHeader} from '../../../containers/filtering/components/FilterNodeTypeDateRangeCalendarHeader';

type Props = {
  config: FilterConfig;
  optional?: boolean;
  value: string | null | undefined;
  onChange?: (newValue: string | null) => void;
  dateOnly?: boolean;
  error?: ValidateError;
  isDefaultValue?: boolean;
  placement?: Placement;
  enableFuture?: boolean;
  disablePast?: boolean;
  style?: React.CSSProperties;
  disabled?: boolean;
  showClearButton?: boolean;
};
export const LightDateFilter: React.FC<Props> = (props) => {
  const {
    config: _config,
    optional,
    value,
    onChange,
    dateOnly,
    error,
    isDefaultValue,
    placement = 'bottom-start',
    enableFuture,
    disablePast,
    style,
    disabled,
    showClearButton,
  } = props;
  const filterType = dateOnly ? FILTER_TYPE_DATE_ONLY_RANGE : FILTER_TYPE_DATE_RANGE;
  const {t} = useFormatters();
  const config = {
    ..._config,
    ui: {
      label: t(_config.ui.label),
      placeholder: _config.ui.placeholder ? t(_config.ui.placeholder) : _config.ui.placeholder,
      type: filterType,
    },
  };
  const [isOpened, setIsOpened] = useState(false);
  const toggleOpened = useCallback(() => {
    setIsOpened((v) => !v);
  }, []);
  const DateRangeFilter = DynamicFilter.getFilterClass(filterType);
  const filter = value
    ? new DateRangeFilter({
        type: filterType,
        property: config.name,
        value,
      })
    : undefined;
  const changeFilter = useCallback(
    (newValue: Date | null) => {
      if (onChange)
        onChange(
          newValue && isValid(newValue)
            ? format(newValue, dateOnly ? DateUtils.ISO_DATE_ONLY_FORMAT : DateUtils.ISO_DATE_TIME_FORMAT)
            : null
        );
    },
    [onChange, dateOnly]
  );
  const removeFilter = useCallback(
    (_newFilter: DateRangeFilter | null) => {
      if (onChange) onChange(null);
    },
    [onChange]
  );
  const inputRef = useCallback(() => {
    // do nothing
  }, []);
  const setFocus = useCallback(() => 0, []);
  const node = new FilterNode(config);

  const theme = useTheme();
  const intl = useIntl();
  const translatedPlaceholder = config.ui.placeholder ? TranslateUtils.translate(config.ui.placeholder, intl) : undefined;
  const clearTranslatedMessage = TranslateUtils.translate('autoLayout.button.remove', intl);
  const muiStyles = dateRangeGetStyles({
    theme,
    params: {fullWidth: true, hasError: error?.bool ?? false},
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isPopperOpened = Boolean(anchorEl);
  const mouseEventClick = useCallback(
    (event) => {
      if (disabled) return;
      setAnchorEl(isPopperOpened ? null : event.currentTarget);
    },
    [disabled, isPopperOpened]
  );
  const handleDatePickerClose = () => {
    if (isPopperOpened) {
      setAnchorEl(null);
    }
  };
  const heightMediaMatches = useMediaQuery('(min-height:800px)');
  const widthMediaMatches = useMediaQuery('(min-width:1800px)');
  const dateValue = value ? parseISO(value) : null;
  const formattedValue = dateValue ? DateUtils.getFormattedDate(dateValue, intl, true, false) : null;
  const styles = getStyles({theme});
  return (
    <span
      className={classNames(lightFilterNodeStyles.dlFilterNodeContent, 'dlFilterNode')}
      css={muiStyles.singleDateFilterWrapper}
      data-name={config.name}
      style={style}
    >
      <FilterNodeTypeWrapper
        filter={filter}
        isOpened={isOpened}
        node={node}
        removeFilter={removeFilter}
        setFocus={setFocus}
        selectedFilter={filter}
        // suppressFocus={suppressFocus}
        toggleOpened={toggleOpened}
        isMui
      >
        <div css={muiStyles.textFieldWrapper}>
          <DlMuiTextField
            data-name={'filter-node-type-date-range--input'}
            data-error={Boolean(error?.bool)}
            label={config.ui.label ? <LightInputLabel text={config.ui.label} optional={optional} intl={intl} /> : ''}
            placeholder={translatedPlaceholder}
            variant={'outlined'}
            onClick={disabled ? undefined : mouseEventClick}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position='end'>
                  {showClearButton && formattedValue && !disabled && (
                    <DlMuiDynamicIcon
                      iconName={'clearIcon'}
                      onClick={(event) => {
                        event.stopPropagation();
                        removeFilter(null);
                      }}
                      svgIconInputCss={styles.clearIcon}
                      noButtonStyles
                      iconTitle={clearTranslatedMessage}
                    />
                  )}
                  <DlMuiCalendarIcon />
                </InputAdornment>
              ),
            }}
            value={formattedValue ?? ''}
            inputCss={muiStyles.textField}
            error={error?.bool}
            helperText={error?.string}
            isDefaultValue={isDefaultValue}
            disabled={disabled}
          />
        </div>
        {isPopperOpened ? (
          <ClickAwayListener
            mouseEvent='onMouseDown'
            onClickAway={(event) => {
              if (anchorEl && anchorEl.contains(event.target as HTMLElement)) {
                return;
              }
              handleDatePickerClose();
            }}
          >
            <Popper
              data-name={'dl-mui-popper--popper'}
              open={isPopperOpened}
              anchorEl={heightMediaMatches && widthMediaMatches ? anchorEl : null}
              placement={placement}
              css={muiStyles.popper}
              disablePortal
            >
              <div css={muiStyles.dateRangeWrapper}>
                <DlMuiDatePicker
                  data-name={'filter-node-type-date-range--start'}
                  slotProps={{
                    actionBar: {hidden: true},
                  }}
                  slots={{
                    layout: (layoutProps) =>
                      DlMuiDatePickerLayout(layoutProps, {
                        styles: muiStyles,
                        dateFrom: true,
                        intl,
                        dateDifference: undefined,
                        isSingleDate: true,
                      }),
                    day: (dayProps) =>
                      DlMuiPickersDay({
                        ...dayProps,
                        endDate: null,
                        startDate: dateValue,
                        isStartDate: true,
                      }),
                    calendarHeader: (headerProps) => (
                      <FilterNodeTypeDateRangeCalendarHeader enableFuture={enableFuture} {...headerProps} />
                    ),
                  }}
                  value={dateValue}
                  onChange={(value) => {
                    changeFilter(value);
                  }}
                  maxDate={enableFuture ? undefined : today}
                  disablePast={disablePast}
                />
              </div>
            </Popper>
          </ClickAwayListener>
        ) : null}
      </FilterNodeTypeWrapper>
    </span>
  );
};

const getStyles: GetStylesFunction = (options) => ({
  clearIcon: css`
    width: 16px;
    height: 16px;
    color: ${options?.theme?.palette.custom.coolGray['500']};
  `,
});
